/**
 * AnalyticsProvider implementation backed by Mixpanel.
 *
 * Implements the AnalyticsProvider interface from @meiuca/analytics, so the
 * canonical Analytics Service can fan out to Mixpanel alongside Firebase/GA4.
 *
 * Dependency Inversion: Mixpanel depends on @meiuca/analytics (the interface),
 * never the other way around.
 */

import type {
  AnalyticsProvider,
  EventProperties,
  UserProperties,
} from '@meiuca/analytics';

import { getMixpanel, getMixpanelConfig, isMixpanelEnabled } from './mixpanel';

export interface MixpanelAnalyticsOptions {
  /**
   * Master on/off switch, wired to VITE_MIXPANEL_ENABLED. When false (the
   * default), Mixpanel never initializes — the SDK doesn't even load. Set it
   * per environment: `true` in prod, `false` in the dev/staging .env.
   */
  enabled?: boolean;
}

export class MixpanelAnalyticsProvider implements AnalyticsProvider {
  private initialized = false;
  private enabled = false;

  constructor(private readonly options: MixpanelAnalyticsOptions = {}) {}

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Master switch (VITE_MIXPANEL_ENABLED) — off in every env unless explicitly on.
    if (!this.options.enabled) {
      console.log('[MIXPANEL_ANALYTICS_PROVIDER] Mixpanel disabled (VITE_MIXPANEL_ENABLED is not true)');
      this.initialized = true;
      return;
    }

    if (!isMixpanelEnabled()) {
      console.warn('[MIXPANEL_ANALYTICS_PROVIDER] Mixpanel enabled but no token configured, staying disabled');
      this.initialized = true;
      return;
    }

    try {
      const config = getMixpanelConfig()!;

      const initOptions: Record<string, unknown> = {
        debug: config.debug ?? false,
        persistence: 'localStorage',
        // We emit screen_view ourselves via the Analytics Service; don't double-count.
        track_pageview: false,
        // Honor the browser Do Not Track signal in addition to the LGPD opt-out.
        ignore_dnt: false,
      };
      // Only set api_host when provided (EU data residency for LGPD).
      if (config.apiHost) {
        initOptions.api_host = config.apiHost;
      }

      getMixpanel().init(config.token, initOptions);
      this.enabled = true;
      console.log('[MIXPANEL_ANALYTICS_PROVIDER] Mixpanel initialized successfully');
      this.initialized = true;
    } catch (error) {
      console.error('[MIXPANEL_ANALYTICS_PROVIDER] Failed to initialize:', error);
      this.initialized = true;
    }
  }

  logEvent(eventName: string, properties?: EventProperties): void {
    if (!this.enabled) {
      return;
    }

    try {
      getMixpanel().track(eventName, properties as Record<string, unknown>);
    } catch (error) {
      console.error(`[MIXPANEL_ANALYTICS_PROVIDER] Error logging event ${eventName}:`, error);
    }
  }

  setUserId(userId: string | null): void {
    if (!this.enabled) {
      return;
    }

    try {
      if (userId) {
        getMixpanel().identify(userId);
      } else {
        // Logout: clear identity and start a fresh anonymous distinct_id.
        getMixpanel().reset();
      }
    } catch (error) {
      console.error('[MIXPANEL_ANALYTICS_PROVIDER] Error setting user ID:', error);
    }
  }

  setUserProperties(properties: UserProperties): void {
    if (!this.enabled) {
      return;
    }

    try {
      getMixpanel().people.set(properties as Record<string, unknown>);
    } catch (error) {
      console.error('[MIXPANEL_ANALYTICS_PROVIDER] Error setting user properties:', error);
    }
  }

  logPageView(pageName: string, pageLocation?: string): void {
    if (!this.enabled) {
      return;
    }

    try {
      getMixpanel().track('page_view', {
        page_title: pageName,
        page_location: pageLocation ?? window.location.href,
      });
    } catch (error) {
      console.error('[MIXPANEL_ANALYTICS_PROVIDER] Error logging page view:', error);
    }
  }

  setCurrentScreen(screenName: string): void {
    if (!this.enabled) {
      return;
    }

    try {
      // Register as a super property so every subsequent Mixpanel event carries
      // the current screen (the Analytics Service also injects it per-event).
      getMixpanel().register({ screen: screenName });
    } catch (error) {
      console.error('[MIXPANEL_ANALYTICS_PROVIDER] Error setting current screen:', error);
    }
  }

  /**
   * LGPD opt-out. The Analytics Service already gates dispatch when tracking is
   * disabled; this also flips Mixpanel's own opt-in/opt-out state.
   */
  setConsent(granted: boolean): void {
    if (!this.enabled) {
      return;
    }

    try {
      if (granted) {
        getMixpanel().opt_in_tracking();
      } else {
        getMixpanel().opt_out_tracking();
      }
    } catch (error) {
      console.error('[MIXPANEL_ANALYTICS_PROVIDER] Error setting consent:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
